import React, { useEffect } from "react";
import "./SellMyHome.scss";
import { Link } from "react-router-dom";

import Appbar from "../../components/Appbar/Appbar";
import Footer from "../../components/Footer/Footer";
import Form from "../../components/Form/Form";

import { Box, Typography, Button } from "@mui/material";
import { useAnalytics } from "../../customHooks/reactGA";

import iconLight from "../../assets/carp_house_plain.png";
import iconDark from "../../assets/carp-house-plain-dark.png";

const steps = [
  {
    title: "Contact Us",
    body: `The first step is easy — just reach out to us! You can fill out our online form, give us a call, or send us a message. As a local Indiana business, we are always just a phone call away, ready to assist you with any questions you may have.

We'll listen to your situation and gather a few details about your property — no pressure, no obligations. Whether you're dealing with a foreclosure, a house that's in need of repairs, or simply need to sell quickly, we’re here to help.`,
  },
  {
    title: "We Schedule a No-Obligation Property Visit",
    body: `After you contact us, one of our team members — who’s local and familiar with your area — will schedule a time to meet with you in person. This allows us to evaluate the property and make sure we understand its condition, location, and any unique aspects that may affect its value.

No need to worry about cleaning, fixing, or preparing your home. We’ll meet you at your convenience, with no pressure to sell.`,
  },
  {
    title: "Receive Your Fair Cash Offer",
    body: `Once we’ve seen the property, we’ll make you a fair, all-cash offer based on our assessment. Our offers are no-obligation — you’re free to accept or decline, and we won’t pressure you in any way. We pride ourselves on being transparent and honest throughout the process, so you can rest assured that the offer we make is a fair reflection of your home’s value.

Since we are buying the property directly from you, there are no realtor commissions or hidden fees involved. Everything is upfront, and there are no surprises.`,
  },
  {
    title: "We Close On Your Timeline",
    body: `Once you accept our cash offer, we can close on your timeline. Whether you need to close in a few days or a few months, we work with you to make it happen.

Our local team will handle all the paperwork and logistics, ensuring a smooth and easy process. Because we’re a cash buyer, there’s no waiting for bank approvals or financing — we can close in as little as 7 days, or whenever works best for you.`,
  },
  {
    title: "Get Paid",
    body: `Once the paperwork is complete and the sale is closed, you’ll receive your payment on the day of closing. Since there is no bank involved, you do not have to worry about any bank approvals or hangups on the day of closing — we make sure you get your money quickly so you can move forward with your next chapter.`,
  },
];

const SellMyHome = (props) => {
  const { trackPageView, trackEvent } = useAnalytics();

  useEffect(() => {
    trackPageView("/sell-my-home");
  }, [trackPageView]);

  return (
    <Box>
      <Appbar colorMode={props.colorMode} theme={props.theme} mode={props.mode} />

      <Box
        component="section"
        className="landing"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark" ? theme.palette.primary.dark : "#e4e4e4",
          backgroundImage: `url(${theme.palette.mode === "dark" ? iconDark : iconLight})`,
          backgroundRepeat: "no-repeat",
          backgroundPosition: "center",
          backgroundSize: "contain",
          px: { xs: 3, md: 7 },
          py: { xs: 6, md: 8 },
        })}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
            alignItems: "center",
            gap: { xs: 5, md: 6 },
            maxWidth: "1350px",
            width: "100%",
            mx: "auto",
          }}
        >
          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Typography
              component="h1"
              className="heading-title l1 heading-title--pop"
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.warning.main
                    : theme.palette.primary.main,
              })}
            >
              We buy As-is… and we mean it!
            </Typography>

            <Typography
              component="p"
              className="heading-title l2"
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.dark,
                maxWidth: "560px",
              })}
            >
              At CarpHomes, we believe every home has value. We’re ready to make
              you a fair, all-cash offer in the exact as-is condition of your
              home. You don’t need to worry about making repairs, cleaning, or
              even staging your home. We can save you time, money, and stress.
            </Typography>
          </Box>

          <Box sx={{ width: "100%", minWidth: 0 }}>
            <Box
              sx={(theme) => ({
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                overflow: "hidden",
                p: { xs: 2.5, md: 4 },
                borderRadius: "24px",
                backgroundColor:
                  theme.palette.mode === "dark"
                    ? "rgba(0, 0, 0, 0.25)"
                    : "rgba(255, 255, 255, 0.72)",
                boxShadow:
                  theme.palette.mode === "dark"
                    ? "0 20px 45px rgba(0, 0, 0, 0.25)"
                    : "0 20px 45px rgba(0, 0, 0, 0.12)",
                backdropFilter: "blur(4px)",

                "& form": {
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                },

                "& .MuiFormControl-root": {
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                },

                "& .MuiInputBase-root": {
                  width: "100%",
                  maxWidth: "100%",
                  boxSizing: "border-box",
                },
              })}
            >
              <Typography
                sx={(theme) => ({
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.primary.main,
                  fontSize: { xs: "1.5rem", md: "2rem" },
                  fontFamily: "karla",
                  mb: 2,
                  textAlign: "center",
                })}
              >
                Get Your Free, No-Obligation Cash Offer Today
              </Typography>

              <Form theme={props.theme} />
            </Box>
          </Box>
        </Box>
      </Box>

      <Box
        className="smh-heading-container"
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          flexDirection: { xs: "column", md: "row" },
          gap: 4,
          maxWidth: "1200px",
          mx: "auto",
          my: 10,
          px: { xs: 3, md: 5 },
        }}
      >
        {[
          {
            title: "No Realtor Fees, No Closing Costs",
            body: "When you sell your home to CarpHomes, you won’t pay any realtor commissions, and there are no hidden closing costs. No need for any complicated calculations or last-minute surprises at the closing table. With our offers, what you see is what you get!",
          },
          {
            title: "Local Service, Personal Touch",
            body: "Being local means we care about the community and its people. Unlike big, national companies, we offer a personal, hands-on approach. A member of our team will meet you in person to discuss your needs and walk you through the process, ensuring that you feel comfortable and well-informed every step of the way. When you work with CarpHomes, you’re not just another transaction — you’re a valued neighbor.",
          },
        ].map((item) => (
          <Box key={item.title} flex={1}>
            <Typography variant="h4" fontFamily="karla" mb={2.5} color="warning.main">
              {item.title}
            </Typography>

            <Typography
              lineHeight={2.5}
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.dark,
              })}
            >
              {item.body}
            </Typography>
          </Box>
        ))}
      </Box>

      <Box
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark" ? theme.palette.primary.dark : "#e4e4e4",
          py: 10,
          px: { xs: 3, md: 5 },
        })}
      >
        <Box className="content-wrapper">
          <Box sx={{ width: { xs: "100%", md: "87.5%" }, mx: "auto", textAlign: "center" }}>
            <Typography
              variant="h4"
              fontFamily="karla"
              mb={2.5}
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.secondary.light
                    : theme.palette.primary.dark,
              })}
            >
              How It Works
            </Typography>

            <Typography
              mb={10}
              lineHeight={2.5}
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.dark,
              })}
            >
              Selling your home to CarpHomes is simple, fast, and stress-free.
              We make the process as easy as possible, from start to finish, so
              you can get the cash you need without the hassle of traditional
              real estate transactions. Here's how it works:
            </Typography>
          </Box>

          <Box className="flex-card-container">
            {steps.map((step, index) => (
              <Box
                key={step.title}
                className="flex-card"
                sx={(theme) => ({
                  border:
                    theme.palette.mode === "dark"
                      ? `2px solid ${theme.palette.secondary.main}`
                      : `2px solid ${theme.palette.primary.main}`,
                })}
              >
                <Box className="card-number-wrapper">
                  <Box
                    className="card-number"
                    sx={(theme) => ({
                      backgroundColor:
                        theme.palette.mode === "dark"
                          ? theme.palette.secondary.main
                          : theme.palette.primary.main,
                    })}
                  >
                    <h1>{index + 1}</h1>
                  </Box>
                </Box>

                <Box className="card-text-container">
                  <Typography
                    fontSize="1.25rem"
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                    })}
                  >
                    <span>{step.title}</span>
                  </Typography>

                  <Typography
                    mt={1.25}
                    sx={(theme) => ({
                      whiteSpace: "pre-line",
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.main,
                    })}
                  >
                    {step.body}
                  </Typography>
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      </Box>

      <Box
        component="section"
        className="sell-my-home"
        sx={(theme) => ({
          backgroundColor:
            theme.palette.mode === "dark"
              ? theme.palette.secondary.main
              : theme.palette.primary.main,
          px: { xs: 3, md: 7 },
          py: 10,
          textAlign: "center",
        })}
      >
        <Typography
          sx={(theme) => ({
            color:
              theme.palette.mode === "dark"
                ? theme.palette.primary.main
                : theme.palette.common.white,
            fontSize: "2rem",
            fontFamily: "karla",
          })}
        >
          Ready to sell? Let us make you an offer today.
        </Typography>

        <Box sx={{ mt: "2.5em" }}>
          <Button
            variant="contained"
            color="warning"
            sx={{ px: "25px", py: "15px" }}
            onClick={() => trackEvent("CTA", "Click", "Get Your Cash Offer Now")}
          >
            <Link className="link-item" to="/contact">
              <Typography color="common.white" fontFamily="latoBold" fontSize="1.15rem">
                Get your Cash Offer Now
              </Typography>
            </Link>
          </Button>
        </Box>
      </Box>

      <Box className="content-wrapper">
        <Box component="section" className="contact-form">
          <Box mt={15} mb={10}>
            <Typography
              sx={(theme) => ({
                color:
                  theme.palette.mode === "dark"
                    ? theme.palette.common.white
                    : theme.palette.primary.main,
                fontSize: "2rem",
                fontFamily: "karla",
                mb: "0.5em",
                textAlign: "center",
              })}
            >
              Get Your Free, No-Obligation Cash Offer Today
            </Typography>

            <Form theme={props.theme} />
          </Box>

          <Box
            mt={15}
            sx={(theme) => ({
              backgroundColor:
                theme.palette.mode === "dark"
                  ? theme.palette.primary.light
                  : theme.palette.secondary.main,
              borderRadius: "25px 25px 0 0",
            })}
          >
            <Box className="contact-us-text-container">
              <Typography
                variant="h4"
                fontSize="2.25rem"
                fontFamily="karla"
                mb={2.5}
                mt={5}
                sx={(theme) => ({
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.warning.main
                      : theme.palette.primary.dark,
                })}
              >
                Supplemental Services
              </Typography>

              <Typography
                lineHeight={2.5}
                sx={(theme) => ({
                  color:
                    theme.palette.mode === "dark"
                      ? theme.palette.common.white
                      : theme.palette.primary.dark,
                })}
              >
                In addition to our core focus of purchasing properties directly,
                our team offers supplemental services tailored to specific needs:
              </Typography>

              <ul>
                <li>
                  <Typography
                    lineHeight={2.5}
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.dark,
                    })}
                  >
                    <b>Realtor Services - </b>
                    If you're considering selling your home through the traditional
                    market, our team can assist with listing your property and
                    navigating the sales process.
                  </Typography>
                </li>

                <li>
                  <Typography
                    lineHeight={2.5}
                    sx={(theme) => ({
                      color:
                        theme.palette.mode === "dark"
                          ? theme.palette.common.white
                          : theme.palette.primary.dark,
                    })}
                  >
                    <b>Construction Services for Investors - </b>
                    We provide expert construction management services exclusively
                    for real estate investors.
                  </Typography>
                </li>
              </ul>

              <Box mt={10}>
                <Typography
                  variant="h4"
                  fontSize="2.25rem"
                  fontFamily="karla"
                  mb={2.5}
                  sx={(theme) => ({
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.warning.main
                        : theme.palette.primary.main,
                  })}
                >
                  Sell Your Indiana Home Fast with CarpHomes
                </Typography>

                <Typography
                  lineHeight={2.5}
                  mt={2}
                  sx={(theme) => ({
                    color:
                      theme.palette.mode === "dark"
                        ? theme.palette.common.white
                        : theme.palette.primary.dark,
                  })}
                >
                  We make selling your home easy, fast, and stress-free. As a
                  local Indiana business, we offer cash purchases with no realtor
                  commissions or closing costs. Close in as little as 7 days—no
                  repairs, no open houses, no hassle.
                </Typography>
              </Box>

              <Box display="flex" justifyContent="center" mt={5}>
                <Button
                  variant="contained"
                  sx={(theme) => ({
                    backgroundColor:
                      theme.palette.mode === "dark"
                        ? theme.palette.warning.main
                        : theme.palette.primary.main,
                  })}
                  onClick={() => trackEvent("CTA", "Click", "Reach Out")}
                >
                  <Link className="link-item" to="/contact">
                    <Typography color="common.white">Reach Out</Typography>
                  </Link>
                </Button>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      <Footer />
    </Box>
  );
};

export default SellMyHome;
